import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { isValidEmail } from '@/utils/validation';
import { showError, showSuccess } from '@/utils/alert';

const RESEND_COOLDOWN_SECONDS = 60;

interface LoginPageProps {
  onClose?: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    if (cooldown <= 0) {
      clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown > 0]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: `${window.location.origin}/cowork`,
        },
      });
      if (error) {
        if (error.status === 429) throw { isRateLimit: true };
        throw error;
      }
      setLinkSent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      if (err?.isRateLimit || err?.status === 429) {
        showError(t('login.errorRateLimit'));
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        showError(t('login.errorGeneric'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/cowork`,
      },
    });
    if (error) showError(error.message);
  };

  const handleLinkedInLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'linkedin_oidc',
      options: {
        redirectTo: `${window.location.origin}/cowork`,
      },
    });
    if (error) showError(error.message);
  };

  const handleResend = () => {
    if (cooldown > 0) return;
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setIsLoading(true);
    supabase.auth.signInWithOtp({
      email: trimmed,
      options: { emailRedirectTo: `${window.location.origin}/cowork` },
    })
      .then(({ error }) => {
        if (error?.status === 429) {
          showError(t('login.errorRateLimit'));
        } else if (error) {
          showError(t('login.errorGeneric'));
        } else {
          showSuccess(t('login.emailResent'));
        }
        setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => showError(t('login.errorGeneric')))
      .finally(() => setIsLoading(false));
  };

  const handleUseDifferentEmail = () => {
    setLinkSent(false);
    setEmail('');
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-auto"
      style={{ background: '#f8f9fa', animation: 'loginFadeIn 300ms ease-out' }}
    >
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-white transition-all z-10"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div
        className="w-full max-w-[420px] mx-4"
        style={{ animation: 'loginContentIn 500ms ease-out' }}
      >
        <div className="flex justify-center mb-8">
          <Link to="/">
            <img src="/omni-logo-dark.png" alt="Omni Ad" className="h-8 w-auto" />
          </Link>
        </div>

        <div
          className="bg-white rounded-[24px] px-10 py-10"
          style={{
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.06), 0 12px 48px rgba(0,0,0,0.04)',
          }}
        >
          {!linkSent ? (
            <>
              <div className="text-center mb-8">
                <h1
                  className="text-[#101828] text-[28px] mb-2"
                  style={{ fontFamily: 'Instrument Sans, sans-serif', fontWeight: 600 }}
                >
                  {t('login.title')}
                </h1>
                <p className="text-[#667085] text-[15px] leading-relaxed">{t('login.subtitle')}</p>
              </div>

              {/* Social login buttons */}
              <div className="flex flex-col gap-3 mb-4">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full py-3 rounded-xl text-[#344054] text-[15px] font-medium border border-[#e4e7ec] bg-white hover:bg-[#f9fafb] transition-all active:scale-[0.98] flex items-center justify-center gap-3"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
                  Continue with Google
                </button>
                <button
                  type="button"
                  onClick={handleLinkedInLogin}
                  className="w-full py-3 rounded-xl text-[#344054] text-[15px] font-medium border border-[#e4e7ec] bg-white hover:bg-[#f9fafb] transition-all active:scale-[0.98] flex items-center justify-center gap-3"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M15.335 1H2.665A1.665 1.665 0 001 2.665v12.67A1.665 1.665 0 002.665 17h12.67A1.665 1.665 0 0017 15.335V2.665A1.665 1.665 0 0015.335 1zM5.75 14.5H3.5V7.25h2.25V14.5zM4.625 6.25a1.375 1.375 0 110-2.75 1.375 1.375 0 010 2.75zM14.5 14.5h-2.25v-3.75c0-1.125-.75-1.5-1.125-1.5s-1.375.188-1.375 1.5V14.5H7.5V7.25h2.25v1.125C10.125 7.625 11 7.25 12.125 7.25S14.5 8.375 14.5 10.75V14.5z" fill="#0A66C2"/></svg>
                  Continue with LinkedIn
                </button>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-[#e4e7ec]" />
                <span className="text-[13px] text-[#98a2b3]">or</span>
                <div className="flex-1 h-px bg-[#e4e7ec]" />
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label
                    className="block text-[13px] text-[#344054] mb-1.5"
                    style={{ fontWeight: 500 }}
                  >
                    {t('login.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-white text-[15px] text-[#101828] placeholder:text-[#d0d5dd] outline-none rounded-xl border transition-all duration-200"
                    style={{
                      borderColor: focusedField === 'email' ? '#be43ff' : '#e4e7ec',
                      boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(190,67,255,0.08)' : 'none',
                    }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl text-white text-[15px] transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70"
                  style={{
                    fontWeight: 600,
                    background: '#be43ff',
                    boxShadow: '0 1px 2px rgba(190,67,255,0.2)',
                  }}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    t('login.button')
                  )}
                </button>
              </form>

              <p className="text-center text-[14px] text-[#667085] mt-6">
                {t('login.noAccount')}{' '}
                <Link to="/signup" className="text-[#be43ff] hover:text-[#a830e0]" style={{ fontWeight: 600 }}>
                  {t('login.signUp')}
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-5">
                <Mail className="w-8 h-8 text-[#101828]" />
              </div>
              <h2 className="text-[#101828] text-[18px] mb-2 text-center" style={{ fontWeight: 600 }}>
                {t('login.checkEmailTitle')}
              </h2>
              <p className="text-[#667085] text-[14px] text-center mb-5">
                {t('login.checkEmailSubtitle', { email })}
              </p>
              <p className="text-[#667085] text-[13px] text-center mb-4">{t('login.checkEmailReminder')}</p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isLoading || cooldown > 0}
                  className="text-[#be43ff] hover:text-[#a830e0] text-[14px] font-medium disabled:opacity-70"
                >
                  {cooldown > 0 ? `${t('login.resendLink')} (${cooldown}s)` : t('login.resendLink')}
                </button>
                <button
                  type="button"
                  onClick={handleUseDifferentEmail}
                  className="text-[#667085] hover:text-[#344054] text-[14px]"
                >
                  {t('login.useDifferentEmail')}
                </button>
              </div>
              <p className="text-center text-[14px] text-[#667085] mt-6">
                {t('login.noAccount')}{' '}
                <Link to="/signup" className="text-[#be43ff] hover:text-[#a830e0]" style={{ fontWeight: 600 }}>
                  {t('login.signUp')}
                </Link>
              </p>
            </>
          )}
        </div>

        <p className="text-center text-[12px] text-[#98a2b3] mt-6 leading-relaxed max-w-[300px] mx-auto">
          {t('login.privacyNote')}{' '}
          <button type="button" className="text-[#667085] hover:text-[#344054] underline underline-offset-2 transition-colors">
            {t('login.privacyPolicy')}
          </button>
        </p>
      </div>

      <style>{`
        @keyframes loginFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes loginContentIn {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
